<template>
  <div>
    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right" v-if="showFilters">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>
    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <filters
          :camera-filter.sync="cameraFilter"
          @updateCameraFilter="handleCameraFilterUpdate"
          :unit-filter.sync="unitFilter"
          :unit-options="unitOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :alarm-type-filter.sync="alarmTypeFilter"
          :alarm-type-options="alarmTypeOptions"
          :status-filter.sync="statusFilter"
          :start-datetime.sync="startDatetimeFilter"
          :end-datetime.sync="endDatetimeFilter"
          :date-filter.sync="dateFilter"
        />
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="!showFilters && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button
            variant="link"
            size="sm"
            @click="isFilterPanelOpen = false"
            class="text-dark p-0"
          >
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <filters
            :camera-filter.sync="cameraFilter"
            @updateCameraFilter="handleCameraFilterUpdate"
            :unit-filter.sync="unitFilter"
            :unit-options="unitOptions"
            :tenant-filter.sync="tenantFilter"
            :tenant-options="tenantOptions"
            :alarm-type-filter.sync="alarmTypeFilter"
            :alarm-type-options="alarmTypeOptions"
            :status-filter.sync="statusFilter"
            :start-datetime.sync="startDatetimeFilter"
            :end-datetime.sync="endDatetimeFilter"
            :date-filter.sync="dateFilter"
          />
        </div>
        <div class="filters-panel-footer">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="clearSearchFilters()"
            class="text-capitalize"
          >
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyFilters()"
            class="text-capitalize"
          >
            {{ $t("search") }}
            <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <toastification-loading-content ref="loadingToast" />

    <b-card-group
      v-show="showFilters || !isFilterPanelOpen"
      deck
      :class="{ 'mx-2': showFilters, 'm-0': !showFilters }"
    >
      <!-- Table Container Card -->
      <Transition name="slide-left-right">
        <b-card no-body class="m-0" v-if="!hiddenTableGrid">
          <div class="m-2">
            <!-- Table Top -->
            <b-row>
              <!-- Per Page -->
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                v-if="showFilters"
              >
                <label class="text-capitalize">{{ $t("table.show") }}</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label class="text-capitalize">{{ $t("table.entries") }}</label>
              </b-col>

              <!-- Search -->
              <b-col cols="12" :md="showFilters ? '6' : '12'">
                <div class="d-flex align-items-center justify-content-end">
                  <b-form-input
                    v-model="searchQuery"
                    class="d-inline-block mr-1"
                    :placeholder="$t('table.search') + '...'"
                  />

                  <b-button-group v-if="!showFilters">
                    <b-button
                      @click="isFilterPanelOpen = true"
                      class="buttonFilter cursor-pointer d-flex align-items-center"
                      data-cy="filter-btn"
                    >
                      <b-icon-filter-circle-fill scale="1.1" />
                      <span class="align-middle ml-1 text-capitalize">{{
                        $t("filters")
                      }}</span>
                    </b-button>
                    <toggle-clear-filter
                      @clear-search-filters="clearSearchFilters"
                      :filters-count="filtersCount"
                    />
                  </b-button-group>
                  <b-button
                    variant="primary"
                    class="text-capitalize ml-1"
                    type="button"
                    :title="$t('actions.refresh')"
                    @click="refetchData"
                  >
                    <feather-icon icon="RefreshCwIcon" size="15" />
                  </b-button>
                  <b-button
                    v-if="$can('export_history', 'Alarm')"
                    @click="exportExcel()"
                    variant="success"
                    class="text-capitalize ml-1"
                    type="button"
                    :disabled="disableExport"
                  >
                    <feather-icon icon="DownloadCloudIcon" size="15" />
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </div>

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
            @row-selected="onRowSelected"
            select-mode="single"
            selectable
            @refreshed="onTableRefreshed"
          >
            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>

            <template #cell(alarm_status)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <div
                    :style="`background: ${data.item.alarm_level.color};
                            width: 1.5rem;
                            height: 1.5rem;
                            border-radius: 1rem;
                            text-align: center;`"
                  ></div>
                </template>
                <strong>{{ data.item.unit ? data.item.unit.description : "" }}</strong>
                <br />
                <small>{{ data.item.alarm_status }}</small>
              </b-media>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted text-capitalize">
                  {{
                    $t("table.showing", {
                      from: dataMeta.from,
                      to: dataMeta.to,
                      of: dataMeta.of,
                    })
                  }}
                </span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <Pagination
                  :totalList="totalList"
                  :currentPage="currentPage"
                  @updatePagination="update"
                  :perPage="perPage"
                >
                </Pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </Transition>

      <!-- Modal -->
      <Transition name="slide-right-left" v-if="selectedObject || loadingSelected">
        <b-card
          class="mr-0"
          :class="!showFilters ? 'showScroll ml-0' : 'sticky-card ml-2'"
          no-body
        >
          <feather-icon
            icon="XIcon"
            class="mr-2 ml-auto mt-1"
            style="cursor: pointer; position: absolute; right: 0px; z-index: 10"
            size="30"
            @click="closeIfoSlot()"
          />
          <div>
            <!-- Info -->
            <info-slot :data="selectedObject" v-if="selectedObject" />
            <b-spinner
              v-else
              label="Spinning"
              variant="primary"
              class="spinner"
            ></b-spinner>
          </div>
        </b-card>
      </Transition>
    </b-card-group>

    <assign-task-modal
      v-if="showFilters"
      modal-id="alarm-assign-task-modal"
      :alarm="selectedAlarmForTask"
      :reopen-mode="isReopenMode"
      @task-assigned="handleTaskAssigned"
      @hidden="onAssignTaskModalHidden"
    />

    <alarm-action-modal v-if="showFilters" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BBadge,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import useList from "./useList";
import Filters from "./Filters.vue";
import { ref, watch } from "@vue/composition-api";
import store from "@/store";
import InfoSlot from "../components/GridView/layouts/slots/InfoSlot";
import { SOURCES } from "@/config/layoutConfig";
import Pagination from "@/components/Pagination.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import axios from "@axios";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import AlarmActionModal from "../components/GridView/widgets/AlarmActionModal.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";
import AssignTaskModal from "./AssignTaskModal.vue";
import router from '@/router'

export default {
  components: {
    BreadcrumbMenu,
    Filters,
    InfoSlot,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    BBadge,
    ToastificationLoadingContent,
    FilterModal,
    ButtonFilters,
    ToggleClearFilter,
    BSpinner,
    AlarmActionModal,
    AssignTaskModal,
  },
  props: {
    filters: {
      type: Object | Array,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    slotFilters: {
      type: Object | Array,
      default: () => ({})
    },
  },
  setup(props, context) {
    const useListRef = useList(props.showFilters);
    const openAlarmDetails = ref(false);

    const update = (data) => {
      useListRef.currentPage.value = data;
    };

    if (props.showFilters) useListRef.initFilters();

    if (router.currentRoute.query.id_alarm) {
      useListRef.searchQuery.value = router.currentRoute.query.id_alarm
      setTimeout(() => {
        openAlarmDetails.value = true
      }, 1000)
    }

    const handleCameraFilterUpdate = (newFilter) => {
      useListRef.cameraFilter.value = newFilter;
    };

    const isFilterPanelOpen = ref(false);
    const hiddenTableGrid = ref(false);
    const selectedAlarmForTask = ref(null);
    const isReopenMode = ref(false);

    watch(
      () => store.state.grid.selectedAlarm,
      (selected) => {
        if (selected?.action === "reopen" && selected?.alarm) {
          selectedAlarmForTask.value = selected.alarm;
          isReopenMode.value = true;
          context.root.$nextTick(() => {
            context.root.$bvModal.show("alarm-assign-task-modal");
          });
        }
      }
    );

    const openAssignTaskModal = (alarmItem) => {
      isReopenMode.value = false;
      selectedAlarmForTask.value = alarmItem;
      context.root.$bvModal.show("alarm-assign-task-modal");
    };

    const onAssignTaskModalHidden = () => {
      if (isReopenMode.value) {
        store.dispatch("grid/unsetSelectedAlarm");
        isReopenMode.value = false;
      }
    };

    const handleTaskAssigned = () => {
      useListRef.refetchData();
    };

    const applyFilters = () => {
      useListRef.buildFilters();
      isFilterPanelOpen.value = false;
    };

    const closeIfoSlot = () => {
      useListRef.selectedObject.value = null;
      useListRef.loadingSelected.value = false;
      hiddenTableGrid.value = false;
    };

    const onRowSelected = async (item) => {
      useListRef.selectedObject.value = null;
      if (!item.length) {
        useListRef.loadingSelected.value = false;
        return;
      }

      useListRef.loadingSelected.value = true;

      if (!props.showFilters) {
        hiddenTableGrid.value = true;
      }

      const { data } = await axios.get(item[0].details_link);
      useListRef.selectedObject.value = {
        data: {
          ...data.data,
          in_app_source: SOURCES.ALARM,
        },
      };
      useListRef.loadingSelected.value = false;
    };

    const exportExcel = () => {
      useListRef.action.value = "export";
      useListRef.disableExport.value = true;

      useListRef.fetchList();
    };

    useListRef.applyChartFilters(props.filters);

    watch(
      () => {
        const f = props.slotFilters?.filters ?? props.slotFilters
        return f && Object.keys(f).length ? f : null
      },
      (val) => {
        if (!val) return
        const f = val
        useListRef.statusFilter.value = f.status || []
        useListRef.tenantFilter.value = f.tenant || null
        useListRef.unitFilter.value = f.unit || null
        useListRef.cameraFilter.value = f.cameraArray || null
        useListRef.alarmTypeFilter.value = f.alarmType || null
        useListRef.startDatetimeFilter.value = f.startDate || null
        useListRef.endDatetimeFilter.value = f.endDate || null
        useListRef.filters.value = {
          status: useListRef.statusFilter.value,
          tenant: useListRef.tenantFilter.value,
          unit: useListRef.unitFilter.value,
          alarm: useListRef.alarmTypeFilter.value,
          camera: useListRef.cameraFilter.value,
          from: useListRef.startDatetimeFilter.value,
          to: useListRef.endDatetimeFilter.value,
        }
      },
      {
        immediate: true,
        deep: true
      }
    )

    const onTableRefreshed = (items) => {
      if (openAlarmDetails.value) {
        openAlarmDetails.value = false
        const list = useListRef.refListTable.value.localItems
        onRowSelected([list[0]]);
      }
    };

    return {
      handleCameraFilterUpdate,
      onRowSelected,
      exportExcel,
      update,
      hiddenTableGrid,
      closeIfoSlot,
      isFilterPanelOpen,
      applyFilters,
      selectedAlarmForTask,
      isReopenMode,
      openAssignTaskModal,
      onAssignTaskModalHidden,
      handleTaskAssigned,
      onTableRefreshed,

      ...useListRef,
    };
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/utilities/transitions";

.sticky-card {
  position: sticky;
  top: 6rem;
  height: 80vh;
  overflow: auto;
}

.spinner {
  margin-top: calc(50% - 100px);
  margin-left: 50%;
}

.showScroll {
  position: sticky;
  top: 6rem;
  height: auto;
  overflow: hidden;
}

.filters-panel-fullscreen {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 0;
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.filters-panel-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.dark-layout {
  .filters-panel-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .filters-panel-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .filters-panel-footer {
    border-top-color: #3b4253;
  }

  .filters-panel-content {
    &::-webkit-scrollbar-track {
      background: #161d31;
    }

    &::-webkit-scrollbar-thumb {
      background: #3b4253;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #4b5563;
    }
  }
}
</style>
